import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Printer, ArrowLeft, CheckCircle, Stethoscope, TrendingUp,
  Globe, ShieldCheck, Sparkles, ArrowRight, MessageCircle, Mail, Share2, Copy, Check, Coins
} from 'lucide-react';
import { COMPARE_ROWS } from '@/data/compareRows';

const SHARE_URL = 'https://docsite.care/business-case';
const SHARE_MESSAGE = (
  `I came across DocSite — a direct-to-patient digital practice platform built for individual doctors. ` +
  `Sharing the one-pager business case here so you can have a look:\n\n${SHARE_URL}\n\n` +
  `Free tier is genuinely free forever. No aggregator commissions, own your Google ranking, structured scheduling.`
);

/**
 * Printable one-pager the doctor can hand to a spouse / accountant / hospital
 * administrator. Uses the same COMPARE_ROWS source so copy stays in sync with
 * the Product Brochure. Layout optimised for A4 portrait + screen viewing.
 *
 * Print path: the doctor hits "Print / Save as PDF" → `window.print()`.
 * `@media print` rules hide nav/footer/CTAs and preserve page breaks.
 */
export default function BusinessCasePage() {
  const [copied, setCopied] = React.useState(false);

  useEffect(() => {
    // auto-scroll to top when navigated here
    window.scrollTo(0, 0);
  }, []);

  const doPrint = () => window.print();

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const mailHref = `mailto:?subject=${encodeURIComponent('DocSite — business case for your practice')}&body=${encodeURIComponent(SHARE_MESSAGE)}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'DocSite — Business Case',
          text: SHARE_MESSAGE,
          url: SHARE_URL,
        });
      } catch { /* user cancelled */ }
    } else {
      copyLink();
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard denied */ }
  };

  const todayIso = new Date().toISOString().slice(0, 10);

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>DocSite Business Case for Your Practice — printable</title>
        <meta name="description" content="A printable one-pager you can share with your hospital administrator or practice manager, showing what DocSite delivers across service quality and patient acquisition." />
        <meta name="robots" content="noindex" />
      </Helmet>

      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 14mm 12mm; }
          html, body { background: white !important; }
          .no-print { display: none !important; }
          .print-page { max-width: 100% !important; padding: 0 !important; box-shadow: none !important; }
          .print-row { break-inside: avoid; }
          .print-section { break-inside: avoid-page; }
        }
      `}</style>

      {/* Screen-only action bar */}
      <div className="no-print sticky top-0 z-40 bg-white border-b border-slate-200 py-3 px-4">
        <div className="max-w-5xl mx-auto flex items-center justify-between gap-3">
          <Link to="/product-brochure" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700" data-testid="biz-case-back">
            <ArrowLeft size={14} /> Back to brochure
          </Link>
          <button onClick={doPrint} data-testid="biz-case-print-btn"
                  className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700">
            <Printer size={14} /> Print / Save as PDF
          </button>
        </div>
      </div>

      <div className="print-page max-w-4xl mx-auto bg-white p-8 md:p-12 my-6 shadow-sm print:my-0 print:shadow-none print:p-0" data-testid="business-case-page">

        {/* Header */}
        <header className="flex items-start justify-between mb-8 pb-6 border-b-2 border-slate-900 print-section">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-sky-600">DocSite · Business Case</p>
            <h1 className="font-medium text-slate-900 mt-2 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif', fontSize: 'clamp(2rem, 3.2vw, 3.75rem)', lineHeight: 1.1 }}>
              What DocSite delivers<br />for your practice
            </h1>
          </div>
          <div className="text-right text-xs text-slate-500">
            <p className="font-semibold text-slate-900">docsite.care</p>
            <p className="mt-1">Prepared: {todayIso}</p>
          </div>
        </header>

        {/* Opening paragraph */}
        <section className="print-section mb-8">
          <p className="text-slate-700 leading-relaxed" style={{ fontSize: 'clamp(0.95rem, 1.05vw, 1.15rem)' }}>
            DocSite is a <b>direct-to-patient digital practice platform</b> — a product that brings the doctor–patient
            conversation back to the specialist. Individual doctors run their entire practice surface (publishing,
            scheduling, patient data, SEO) without aggregator commissions or hospital-marketing intermediaries. The
            sections below map every DocSite feature to two outcomes you care about: <b>clinical service quality</b> and
            <b> new-patient acquisition / retention</b>.
          </p>
        </section>

        {/* Three-tier at-a-glance */}
        <section className="print-section mb-10">
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">The three tiers, at a glance</h2>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-xl border border-amber-200 p-4 bg-amber-50/40">
              <p className="text-[10px] uppercase tracking-wider font-bold text-amber-600">Gold</p>
              <p className="text-lg font-bold text-slate-900 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Free</p>
              <p className="text-[11px] text-slate-600 mt-2">Personal website, multi-service scheduling, structured cancellations, PWA install, patient "Scan &amp; Save".</p>
            </div>
            <div className="rounded-xl border border-sky-300 p-4 bg-sky-50/40">
              <p className="text-[10px] uppercase tracking-wider font-bold text-sky-600">Coffee Premium</p>
              <p className="text-lg font-bold text-sky-800 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 299 / mo</p>
              <p className="text-[11px] text-slate-600 mt-2">Adds clinic map, per-procedure pricing, patient chat, DNA tracking, reliability scoring.</p>
            </div>
            <div className="rounded-xl border border-emerald-300 p-4 bg-emerald-50/40">
              <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-600">TurboCharge</p>
              <p className="text-lg font-bold text-emerald-800 mt-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Rs. 999 / mo</p>
              <p className="text-[11px] text-slate-600 mt-2">Adds 2 AI pulls/month, printable QR clinic kit, Auto-SEO landing pages, Visibility Accelerator.</p>
            </div>
          </div>
        </section>

        {/* Per-feature impact rows */}
        <section className="mb-10">
          <h2 className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500 mb-3">Feature-by-feature business impact</h2>
          <div className="space-y-3">
            {COMPARE_ROWS.map((row, i) => (
              <div key={row.label} className="print-row rounded-xl border border-slate-200 p-4" data-testid={`bc-row-${i}`}>
                <div className="flex items-start justify-between gap-3 mb-2.5">
                  <p className="text-sm font-semibold text-slate-900 flex-1">{row.label}</p>
                  <div className="flex items-center gap-1.5 flex-shrink-0 text-[10px] font-bold">
                    {row.free && <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">Gold</span>}
                    {row.premium && <span className="px-2 py-0.5 rounded-full bg-sky-100 text-sky-700">Premium</span>}
                    {row.turbo && <span className="px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">Turbo</span>}
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div className="rounded-lg border border-amber-200 bg-amber-50/60 p-2.5">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-amber-700 mb-1 flex items-center gap-1">
                      <Coins size={11} className="text-amber-600" /> Service Quality
                    </p>
                    <p className="text-xs text-amber-900/90 leading-relaxed">{row.service_impact}</p>
                  </div>
                  <div className="rounded-lg border border-teal-200 bg-teal-50/60 p-2.5">
                    <p className="text-[10px] uppercase tracking-widest font-bold text-teal-700 mb-1 flex items-center gap-1">
                      <Coins size={11} className="text-teal-700" /> Patient Acquisition / Retention
                    </p>
                    <p className="text-xs text-teal-900/90 leading-relaxed">{row.acquisition_impact}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Why DocSite */}
        <section className="print-section mb-10 rounded-xl bg-slate-900 text-white p-5">
          <h2 className="text-sm font-bold text-sky-300 mb-3 flex items-center gap-2">
            <Sparkles size={14} /> Why DocSite vs. an aggregator
          </h2>
          <ul className="text-xs leading-relaxed space-y-1.5">
            <li className="flex items-start gap-2"><CheckCircle size={11} className="mt-0.5 text-emerald-400 flex-shrink-0" /> No commissions on consults. You keep 100% of what you charge.</li>
            <li className="flex items-start gap-2"><CheckCircle size={11} className="mt-0.5 text-emerald-400 flex-shrink-0" /> Your practice surface, your brand, your SEO juice. Not an aggregator's directory, not a hospital's landing page.</li>
            <li className="flex items-start gap-2"><CheckCircle size={11} className="mt-0.5 text-emerald-400 flex-shrink-0" /> No paid "top-of-listing" auctions rigged against smaller practices.</li>
            <li className="flex items-start gap-2"><CheckCircle size={11} className="mt-0.5 text-emerald-400 flex-shrink-0" /> Structured cancellations, audit logs, field-level encryption — enterprise-grade, individual-doctor pricing.</li>
            <li className="flex items-start gap-2"><CheckCircle size={11} className="mt-0.5 text-emerald-400 flex-shrink-0" /> Every patient you acquire stays yours — downloadable patient list, encrypted export.</li>
          </ul>
        </section>

        {/* ====== TURBO EQUATION — bottom-line summary ====== */}
        <section className="print-section mb-10 rounded-xl border-2 border-emerald-600 bg-gradient-to-br from-emerald-50 to-teal-50 p-5" data-testid="bc-turbo-equation">
          <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-[0.2em] mb-2 flex items-center gap-1.5">
            <span aria-hidden="true">🪙</span> Under TurboCharge, the math works for you
          </p>
          <p className="text-xs text-slate-600 mb-3 leading-relaxed">
            Every feature on the ladder above rolls up into a single, compounding equation:
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1.5 font-bold text-emerald-900 py-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <span className="text-base md:text-xl">More Patients</span>
            <span className="text-lg md:text-2xl text-amber-600" aria-hidden="true">×</span>
            <span className="text-base md:text-xl">Loyalty</span>
            <span className="text-lg md:text-2xl text-amber-600" aria-hidden="true">×</span>
            <span className="text-base md:text-xl">Better Health Outcomes</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-3 italic leading-relaxed text-center">
            Compounds month over month — without ad spend, without commissions, without losing your patients to aggregators.
          </p>
        </section>

        {/* Share this business case — screen-only, zero-cost deep-links.
            Uses the doctor's own WhatsApp/email identity — NO platform SMS. */}
        <section className="no-print mb-8 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-sky-50 p-5" data-testid="business-case-share">
          <p className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700 mb-2">Share this business case</p>
          <p className="text-sm text-slate-700 mb-4 leading-relaxed">
            Send it to your hospital administrator, practice manager, or spouse — from your own WhatsApp, email, or phone. Zero platform charges, no SMS.
          </p>
          <div className="flex flex-wrap gap-2">
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500 text-white text-sm font-semibold hover:bg-emerald-600 transition-colors"
               data-testid="bc-share-whatsapp">
              <MessageCircle size={13} /> WhatsApp
            </a>
            <a href={mailHref}
               className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700 transition-colors"
               data-testid="bc-share-email">
              <Mail size={13} /> Email
            </a>
            <button onClick={nativeShare}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
                    data-testid="bc-share-native">
              <Share2 size={13} /> More…
            </button>
            <button onClick={copyLink}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300 transition-colors"
                    data-testid="bc-share-copy">
              {copied ? <><Check size={13} className="text-emerald-600" /> Link copied</> : <><Copy size={13} /> Copy link</>}
            </button>
          </div>
        </section>

        {/* CTA — screen-only */}
        <section className="no-print flex flex-wrap gap-3">
          <Link to="/register?as=doctor&from=biz_case" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800" data-testid="biz-case-start-free">
            <Globe size={14} /> Start on Gold (Free) <ArrowRight size={14} />
          </Link>
          <Link to="/brochure" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white border border-slate-200 text-slate-700 text-sm font-semibold hover:border-slate-300" data-testid="biz-case-view-brochure">
            Full Brochure
          </Link>
          <button onClick={doPrint} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-sky-600 text-white text-sm font-semibold hover:bg-sky-700" data-testid="biz-case-print-bottom">
            <Printer size={14} /> Print this page
          </button>
        </section>

        {/* Footer note */}
        <footer className="mt-10 pt-6 border-t border-slate-200 text-[10px] text-slate-400 leading-relaxed">
          <p className="flex items-center gap-1.5 mb-1"><ShieldCheck size={10} /> Self-declared doctor credentials (NMC / State Council) shown on every public profile. DocSite does not independently verify claims; doctors sign a truthful-declaration consent.</p>
          <p>Prices exclude GST. Cancel anytime. Feature availability on each tier is up-to-date as of {todayIso} and may evolve as the platform grows.</p>
        </footer>
      </div>
    </div>
  );
}
